import { useEffect, useState } from "react";
import "./UserList.css";

export default function UserList() {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentUser, setCurrentUser] = useState({
        name: "",
        email: "",
        mobile: "", // New mobile field added
        password: "",
        role: "user",
        status: "active",
        id: null
    });
    const [errors, setErrors] = useState({});

    // Roles and Status options
    const roles = ["admin", "user", "editor", "viewer"];
    const statuses = ["active", "inactive", "pending"];

    useEffect(() => {
        loadUsers();
    }, []);

const loadUsers = async () => {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/User.php`);
    const data = await res.json();
    setUsers(Array.isArray(data.data) ? data.data : []);
  } catch {
    setUsers([]);
  }
};


const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    try {
        const formData = new URLSearchParams();
        formData.append("id", id);

        const res = await fetch(`${process.env.REACT_APP_API_URL}/User.php`, {
            method: "DELETE",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData.toString()
        });

        const data = await res.json();

        if (res.ok && data.success) {
            alert("User deleted successfully");
            loadUsers();
        } else {
            alert(data.error || "Delete failed");
        }
    } catch (error) {
        console.error(error);
        alert("Server error. Please try again later.");
    }
};


    const openCreateModal = () => {
        setIsEditMode(false);
        setShowModal(true);
        setCurrentUser({
            name: "",
            email: "",
            mobile: "", // Initialize mobile
            password: "",
            role: "user",
            status: "active",
            id: Date.now()
        });
        setErrors({});
    };

    const openEditModal = (user) => {
        setIsEditMode(true);
        setShowModal(true);
        setCurrentUser({
            ...user,
            password: "" // Don't show password in edit mode for security
        });
        setErrors({});
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Mobile number validation (allow only numbers)
        if (name === "mobile") {
            const numericValue = value.replace(/\D/g, '');
            setCurrentUser({
                ...currentUser,
                [name]: numericValue
            });
        } else {
            setCurrentUser({
                ...currentUser,
                [name]: value
            });
        }

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Name validation
        if (!currentUser.name.trim()) {
            newErrors.name = "Name is required";
        } else if (currentUser.name.length < 2) {
            newErrors.name = "Name must be at least 2 characters";
        }

        // Email validation
        if (!currentUser.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(currentUser.email)) {
            newErrors.email = "Email is invalid";
        }

        // Mobile validation (optional but if provided, validate)
        if (currentUser.mobile && currentUser.mobile.length < 10) {
            newErrors.mobile = "Mobile number must be 10 digits";
        }

        // Password validation (only for create mode)
        if (!isEditMode && !currentUser.password.trim()) {
            newErrors.password = "Password is required";
        } else if (!isEditMode && currentUser.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }

        // Role validation
        if (!currentUser.role) {
            newErrors.role = "Role is required";
        }

        // Status validation
        if (!currentUser.status) {
            newErrors.status = "Status is required";
        }

        return newErrors;
    };

    const formatMobileNumber = (mobile) => {
        if (!mobile) return "N/A";
        if (mobile.length === 10) {
            return `+91 ${mobile.slice(0, 5)} ${mobile.slice(5)}`;
        }
        return mobile;
    };

const handleSaveUser = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
        setErrors(validationErrors);
        return;
    }

    try {
        const url = `${process.env.REACT_APP_API_URL}/User.php`;

        // 🔹 EDIT → JSON + PUT
        if (isEditMode) {
            const payload = {
                id: currentUser.id,
                name: currentUser.name,
                email: currentUser.email,
                mobile: currentUser.mobile,
                role: currentUser.role,
                status: currentUser.status
            };

            if (currentUser.password) {
                payload.password = currentUser.password;
            }

            const res = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!res.ok) {
                alert(data.error || `Request failed (${res.status})`);
                return;
            }

            alert(data.message);
            closeModal();
            loadUsers();
            return;
        }

        // 🔹 CREATE → FormData + POST
        const formData = new FormData();
        formData.append("name", currentUser.name);
        formData.append("email", currentUser.email);
        formData.append("mobile", currentUser.mobile);
        formData.append("password", currentUser.password);
        formData.append("role", currentUser.role);
        formData.append("status", currentUser.status);

        const res = await fetch(url, {
            method: "POST",
            body: formData
        });

        const data = await res.json();

        if (!res.ok) {
            if (res.status === 409) {
                alert("Email already exists.");
            } else {
                alert(data.error || `Request failed (${res.status})`);
            }
            return;
        }

        alert(data.message);
        closeModal();
        loadUsers();

    } catch (error) {
        console.error(error);
        alert("Server error. Please try again later.");
    }
};






    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin': return 'role-admin';
            case 'editor': return 'role-editor';
            case 'viewer': return 'role-viewer';
            default: return 'role-user';
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'active': return 'status-active';
            case 'inactive': return 'status-inactive';
            case 'pending': return 'status-pending';
            default: return '';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'Active';
            case 'inactive': return 'Inactive';
            case 'pending': return 'Pending';
            default: return status;
        }
    };

    return (
        <div className="user-list">
            <div className="user-list-header">
                <h2>User Management</h2>
                <button
                    className="create-user-btn"
                    onClick={openCreateModal}
                >
                    <span className="btn-icon">+</span> Create New User
                </button>
            </div>

            <div className="users-container">
                {users.length === 0 ? (
                    <div className="no-users">
                        <div className="no-users-icon">👤</div>
                        <p>No users found</p>
                        <button
                            className="create-first-btn"
                            onClick={openCreateModal}
                        >
                            Create Your First User
                        </button>
                    </div>
                ) : (
                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id} className="user-row">
                                        <td className="user-id">#{user.id.toString().slice(-4)}</td>
                                        <td className="user-name-cell">
                                            <div className="user-avatar">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="user-name-info">
                                                <span className="user-name">{user.name}</span>
                                                <span className="user-email-mobile">{user.email}</span>
                                            </div>
                                        </td>
                                        <td className="user-email">{user.email}</td>
                                        <td className="user-mobile">
                                            {formatMobileNumber(user.mobile)}
                                        </td>
                                        <td>
                                            <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                                                {getStatusText(user.status)}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                className="update-btn"
                                                onClick={() => openEditModal(user)}
                                                title="Edit User"
                                            >
                                                <span className="btn-icon">✎</span> Edit
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => handleDelete(user.id)}
                                                title="Delete User"
                                            >
                                                <span className="btn-icon">🗑</span> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal for Create/Edit User */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{isEditMode ? 'Edit User' : 'Create New User'}</h3>
                            <button
                                className="modal-close-btn"
                                onClick={closeModal}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* First Row: Name and Email */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">
                                        Full Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={currentUser.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        className={errors.name ? "error" : ""}
                                    />
                                    {errors.name && (
                                        <span className="error-message">{errors.name}</span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">
                                        Email <span className="required">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={currentUser.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        className={errors.email ? "error" : ""}
                                    />
                                    {errors.email && (
                                        <span className="error-message">{errors.email}</span>
                                    )}
                                </div>
                            </div>

                            {/* Second Row: Mobile and Password */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="mobile">
                                        Mobile Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="mobile"
                                        name="mobile"
                                        value={currentUser.mobile}
                                        onChange={handleInputChange}
                                        placeholder="Enter 10-digit mobile number"
                                        className={errors.mobile ? "error" : ""}
                                        maxLength="10"
                                    />
                                    {errors.mobile && (
                                        <span className="error-message">{errors.mobile}</span>
                                    )}
                                    <small className="form-hint">Optional - Only numbers allowed</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password">
                                        Password {!isEditMode && <span className="required">*</span>}
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={currentUser.password}
                                        onChange={handleInputChange}
                                        placeholder={isEditMode ? "Leave blank to keep current" : "Enter password"}
                                        className={errors.password ? "error" : ""}
                                    />
                                    {errors.password && (
                                        <span className="error-message">{errors.password}</span>
                                    )}
                                    {isEditMode && (
                                        <small className="form-hint">Leave blank to keep current password</small>
                                    )}
                                </div>
                            </div>

                            {/* Third Row: Role and Status */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="role">
                                        Role <span className="required">*</span>
                                    </label>
                                    <select
                                        id="role"
                                        name="role"
                                        value={currentUser.role}
                                        onChange={handleInputChange}
                                        className={errors.role ? "error" : ""}
                                    >
                                        <option value="">Select Role</option>
                                        {roles.map(role => (
                                            <option key={role} value={role}>
                                                {role.charAt(0).toUpperCase() + role.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.role && (
                                        <span className="error-message">{errors.role}</span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="status">
                                        Status <span className="required">*</span>
                                    </label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={currentUser.status}
                                        onChange={handleInputChange}
                                        className={errors.status ? "error" : ""}
                                    >
                                        <option value="">Select Status</option>
                                        {statuses.map(status => (
                                            <option key={status} value={status}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.status && (
                                        <span className="error-message">{errors.status}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="cancel-btn"
                                onClick={closeModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="submit-btn"
                                onClick={handleSaveUser}
                            >
                                {isEditMode ? 'Update User' : 'Create User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}